import { useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { runStoreCoverageMapV1RunsRunIdStoreCoverageStoreIdMapGet } from "../api/generated/qcsControlPlane";
import type { Platform, StoreGeo } from "../api/generated/model";
import { fmtInt } from "../api/labels";
import { TierPills } from "./tiers";

// A store's reroute, shown geographically. Blinkit/Instamart route by lat/lon, so the
// probed store's location is actually served by some *merchant*; its SKUs were
// reroute-attributed there. We draw just the two, each as a point + serviceable area:
//  • anchor (blue), the probed store, and
//  • serving (amber), the merchant(s) that served its location (their coverage
//    contains the anchor, which is why it was routed to them).

const ANCHOR = "#1f6feb";
const SERVING = "#e3853b";

type Role = "anchor" | "serving";

// The spec types a store boundary as a bare JSON object, so its GeoJSON `type`
// discriminant is only known at runtime. Check it here rather than asserting the
// shape into existence: a boundary that lost its `type` upstream should draw
// nothing, not reach Leaflet as a malformed geometry.
function isGeoJson(
  value: Record<string, unknown>,
): value is Record<string, unknown> & GeoJSON.GeoJsonObject {
  return typeof value.type === "string";
}

function addStore(layer: L.LayerGroup, s: StoreGeo, role: Role): void {
  const color = role === "anchor" ? ANCHOR : SERVING;
  const fillOpacity = role === "anchor" ? 0.12 : 0.18;
  const boundary = s.boundary;
  if (boundary && isGeoJson(boundary)) {
    L.geoJSON(boundary, {
      style: { color, weight: 1.5, fillColor: color, fillOpacity },
    }).addTo(layer);
  }
  const note = role === "serving" ? `<br/>serves this location · ${fmtInt(s.sku_count ?? 0)} SKUs` : "";
  const loc = [s.locality, s.tier].filter(Boolean).join(" · ");
  const sub = loc ? `<br/>${loc}` : "";
  L.circleMarker([s.lat, s.lon], {
    radius: role === "anchor" ? 7 : 5,
    color,
    weight: 2,
    fillColor: color,
    fillOpacity: 0.9,
  })
    .bindPopup(`<b>${s.name || s.store_id}</b> <span style="opacity:.6">${s.store_id}</span>${sub}${note}`)
    .addTo(layer);
}

export function StoreOverlapMap({
  runId,
  storeId,
  platform,
}: {
  runId: string;
  storeId: string;
  platform: Platform | null;
}) {
  const elRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const layerRef = useRef<L.LayerGroup | null>(null);

  const q = useQuery({
    queryKey: ["store-overlap-map", runId, storeId],
    queryFn: () => runStoreCoverageMapV1RunsRunIdStoreCoverageStoreIdMapGet(runId, storeId),
    staleTime: 60_000,
  });

  useEffect(() => {
    if (!elRef.current || mapRef.current) return;
    const map = L.map(elRef.current, { attributionControl: false, preferCanvas: true });
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", { maxZoom: 19 }).addTo(map);
    layerRef.current = L.layerGroup().addTo(map);
    mapRef.current = map;
    setTimeout(() => map.invalidateSize(), 0);
    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    const layer = layerRef.current;
    const data = q.data;
    if (!map || !layer || !data) return;
    layer.clearLayers();
    const pts: L.LatLngTuple[] = [];
    // Serving polygons underneath, then the anchor on top.
    for (const s of data.serving ?? []) {
      addStore(layer, s, "serving");
      pts.push([s.lat, s.lon]);
    }
    if (data.anchor) {
      addStore(layer, data.anchor, "anchor");
      pts.push([data.anchor.lat, data.anchor.lon]);
    }
    if (pts.length) {
      map.invalidateSize();
      map.fitBounds(L.latLngBounds(pts).pad(0.3), { maxZoom: 14 });
    }
  }, [q.data]);

  const serving = q.data?.serving ?? [];
  const swatch = (color: string, label: string) => (
    <span className="row" style={{ gap: 5 }}>
      <span style={{ width: 11, height: 11, borderRadius: 2, background: color }} />
      {label}
    </span>
  );
  return (
    <div style={{ position: "relative" }}>
      <div className="row" style={{ gap: 14, marginBottom: 6, fontSize: 10.5, flexWrap: "wrap" }}>
        {swatch(ANCHOR, "this store")}
        {swatch(SERVING, `routed to (${fmtInt(serving.length)})`)}
      </div>
      <div
        ref={elRef}
        style={{ width: "100%", height: 320, borderRadius: 6, overflow: "hidden", zIndex: 0 }}
      />
      {serving.length > 0 && (
        <div className="col" style={{ gap: 4, marginTop: 8 }}>
          {serving.map((s, i) => (
            <div key={`${s.store_id}-${i}`} className="row" style={{ gap: 8, fontSize: 11, alignItems: "center" }}>
              <span style={{ width: 8, height: 8, borderRadius: 2, background: SERVING, flex: "none" }} />
              <span>{s.name || s.store_id}</span>
              {platform !== null ? (
                <Link to={`/stores/${platform}/${encodeURIComponent(s.store_id)}`} className="faint mono" style={{ fontSize: 10 }} target="_blank" rel="noopener noreferrer" title="Open store page">
                  {s.store_id}
                </Link>
              ) : (
                <span className="faint mono" style={{ fontSize: 10 }}>{s.store_id}</span>
              )}
              {s.locality && <span className="faint">{s.locality}</span>}
              <TierPills tiers={s.tiers} fallback={s.tier} />
              <span className="faint num">served {fmtInt(s.sku_count ?? 0)} SKUs</span>
            </div>
          ))}
        </div>
      )}
      {q.isError ? (
        <p className="faint" style={{ fontSize: 11, marginTop: 6 }}>Could not load the coverage map.</p>
      ) : !q.isLoading && serving.length === 0 ? (
        <p className="faint" style={{ fontSize: 11, marginTop: 6 }}>
          No other store served this store's location.
        </p>
      ) : null}
    </div>
  );
}
